import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useModal } from "@/app/hooks/ui_hooks/useModal";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useModalState } from "@/app/hooks/ui_hooks/useModalState";
import { useTileDetailAPI } from "@/app/hooks/api_hooks/Tiles/useTileDetailAPI";
import { useTileUpdateAPI } from "@/app/hooks/api_hooks/Tiles/useTileUpdateAPI";
import { useTilesListAPI } from "@/app/hooks/api_hooks/Tiles/useTilesListAPI";
import { ITilesUpdateBody, ITilesViewResponseData } from "@/api_framework/api_modals/Tiles";
import { AddTileModalContent, IAddTileFormFields } from "../AddTileModal";

const tileToFormValues = (tile: ITilesViewResponseData): IAddTileFormFields => ({
  tile_name: tile.tile_name,
  type: tile.type,
  collection_id: tile.type === "service" ? tile.match_key || "" : tile.collection_id || "",
  visible: tile.visible,
  persona: tile.persona?.length
    ? tile.persona.map((value) => ({ value, sorting_key: tile.sorting_key?.[value] ?? "" }))
    : [{ value: "", sorting_key: "" }],
  ribbon: tile.ribbon?.length
    ? tile.ribbon.map((row) => ({
        chip_txt: row.chip_txt,
        persona: row.persona?.length ? row.persona.map((value) => ({ value })) : [{ value: "" }],
      }))
    : [{ chip_txt: "", persona: [{ value: "" }] }],
});

const buildUpdatePayload = (values: IAddTileFormFields): ITilesUpdateBody => {
  const personaRows = values.persona.filter((row) => row.value);
  return {
    visible: values.visible,
    persona: personaRows.map((row) => row.value),
    sorting_key: personaRows.reduce<Record<string, number>>((acc, row) => {
      acc[row.value] = Number(row.sorting_key) || 0;
      return acc;
    }, {}),
    ribbon: values.ribbon.map((row) => ({
      chip_txt: row.chip_txt,
      persona: row.persona.map((personaRow) => personaRow.value).filter(Boolean),
    })),
  };
};

const EditTileModal = () => {
  const { setInvisible } = useModalActions("TILE_EDIT_MODAL");
  const isVisible = useModal("TILE_EDIT_MODAL");
  const { tileName, mode } = useModalState("TILE_EDIT_MODAL") as { tileName?: string; mode?: "live" | "draft" };
  const fetchTileDetail = useTileDetailAPI();
  const updateTile = useTileUpdateAPI();
  const fetchTilesList = useTilesListAPI();

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const formHook = useForm<IAddTileFormFields>({
    mode: "onChange",
    defaultValues: {
      type: "",
      visible: true,
      persona: [{ value: "", sorting_key: "" }],
      ribbon: [{ chip_txt: "", persona: [{ value: "" }] }],
    },
  });

  useEffect(() => {
    if (!isVisible || !tileName) return;
    setIsLoading(true);
    fetchTileDetail(tileName)
      .then((tile) => {
        if (tile) formHook.reset(tileToFormValues(tile));
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, tileName]);

  const closeAndReset = () => {
    formHook.reset();
    setInvisible();
  };

  const onUpdate = () => {
    if (!tileName) return;
    formHook.trigger().then((isValid) => {
      if (!isValid) return;
      setIsSaving(true);
      updateTile(tileName, buildUpdatePayload(formHook.getValues()))
        .then((success) => {
          if (success) {
            fetchTilesList(mode ?? "draft");
            closeAndReset();
          }
        })
        .finally(() => setIsSaving(false));
    });
  };

  return (
    <ModalPrompt
      modalId="TILE_EDIT_MODAL"
      contentOverflow={false}
      isLoading={isLoading || isSaving}
      dialogClassName="sm:my-8 sm:w-full sm:max-w-lg sm:p-6 max-h-[70vh] overflow-y-auto"
      button={{
        primaryButtonText: "Update",
        primaryButtonClassname: "bg-[#1a3c87] hover:bg-[#1a3c87]",
        secondaryButtonText: "Cancel",
        primaryButtonDisabled: isLoading || isSaving,
        primaryCallback: onUpdate,
        secondaryCallback: closeAndReset,
        shouldHideOnClick: false,
      }}
      title="Edit Tile"
      content={<AddTileModalContent formHook={formHook} hideIdentityFields />}
    />
  );
};

export default EditTileModal;
